import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { isMobileQuery, primaryBtn, secondaryBtn } from './styles';

// In-call UI shared by host and guest.
//
// Responsibilities:
//   • Establish the RTCPeerConnection over the already-open signaling WS.
//     Host creates the offer once both peers are present; guest answers.
//   • Render local + remote video tiles in one of three layouts.
//   • (Host only) Compose both video streams onto a hidden canvas + mix
//     the audio tracks, then feed the composite to MediaRecorder. Stop
//     produces a single File the host can review and upload.
//
// The WebSocket lifecycle is OWNED by the parent (InterviewHost /
// InterviewJoin). We just attach our message handler; we never close the
// socket here. Same for the local MediaStream — the parent gives it to
// us, we never stop its tracks.

const LAYOUTS = {
  // Two equal tiles. On mobile we stack vertically (portrait-friendly).
  SIDE_BY_SIDE: 'side-by-side',
  // Remote full, local in the corner.
  PIP_LOCAL_CORNER: 'pip-local',
  // Local full, remote in the corner. Useful for talking-head reactions.
  PIP_REMOTE_CORNER: 'pip-remote',
};

// Canvas composition dimensions per layout. The aspect ratio of the
// canvas determines the aspect ratio of the recorded file, so we pick
// sensible defaults — 16:9 for the PIP modes, 16:9 single-row for SBS
// on desktop, 9:16 stacked for SBS on mobile.
const canvasDimsFor = (layout, mobile) => {
  if (layout === LAYOUTS.SIDE_BY_SIDE) {
    return mobile ? { w: 720, h: 1280 } : { w: 1920, h: 540 };
  }
  // PIP modes: standard 720p canvas.
  return { w: 1280, h: 720 };
};

const PIP_TILE_FRACTION = 0.28; // small tile = 28% of the larger axis

export const InterviewCall = ({
  peerRole,
  localName, // this participant's chosen display name (optional)
  localStream,
  ws,
  iceServers,
  onLeave,
  onRecordingFinalized, // (file: File) => void   — host only, fires after Stop
}) => {
  // Display names. Local falls back to the role; remote is learned over the
  // data channel and falls back to the OTHER role.
  const displayLocalName = (localName && localName.trim())
    || (peerRole === 'host' ? 'Host' : 'Guest');
  const [isMobile, setIsMobile] = useState(isMobileQuery);
  // Orientation is independent of the mobile breakpoint — a laptop in a
  // narrow window can also be "portrait" and benefits from the same
  // stacked-tiles layout. The stage uses isPortrait to flip row/column;
  // isMobile is kept for type-size + padding tweaks.
  const [isPortrait, setIsPortrait] = useState(
    typeof window !== 'undefined' ? window.innerHeight > window.innerWidth : false,
  );
  useEffect(() => {
    const onResize = () => {
      setIsMobile(isMobileQuery());
      setIsPortrait(window.innerHeight > window.innerWidth);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const [remoteStream, setRemoteStream] = useState(null);
  const [layout, setLayout] = useState(LAYOUTS.SIDE_BY_SIDE);
  const [callState, setCallState] = useState('connecting');
  // 'connecting' | 'connected' | 'peer-gone' | 'nat-blocked' | 'error'
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  // Local mic mute toggle. We flip `track.enabled` instead of stopping
  // the track — stopping it would force getUserMedia again to resume,
  // and would also tear down browser AEC which depends on the live mic
  // track being present even when "muted".
  // Initial value derives from the incoming track so a pre-join lobby
  // choice (parent disabled the track before handing it over) is honoured.
  const [muted, setMuted] = useState(() => {
    const a = localStream && localStream.getAudioTracks()[0];
    return a ? !a.enabled : false;
  });
  // Tracks which camera we're using so we know which way to flip + which
  // way to mirror the local preview. Front-facing ('user') is mirrored
  // for selfie-style; back-facing ('environment') shows the world as-is.
  // Derive initial value from the live track's settings when possible so
  // a remount (e.g. peer rejoin → key change) doesn't reset the state to
  // 'user' while the actual track is still on the back camera.
  const [facingMode, setFacingMode] = useState(() => {
    if (localStream) {
      const t = localStream.getVideoTracks()[0];
      if (t && t.getSettings) {
        const s = t.getSettings() || {};
        if (s.facingMode === 'environment' || s.facingMode === 'user') return s.facingMode;
      }
    }
    return 'user';
  });
  // True while a switchCamera() call is in flight — disables the button
  // so a user can't fire a second swap before the first settles.
  const [switchingCamera, setSwitchingCamera] = useState(false);
  // Local camera on/off (track.enabled). Declared up here because
  // switchCamera reads it to carry the off-state onto a freshly-swapped
  // track. The toggle handler lives next to toggleMute below.
  // Initial value derives from the incoming track so a pre-join lobby
  // "camera off" choice is honoured.
  const [cameraOff, setCameraOff] = useState(() => {
    const v = localStream && localStream.getVideoTracks()[0];
    return v ? !v.enabled : false;
  });

  // Guards an accidental tap on the Leave button. The guest reached this
  // call via a one-time shared link — a stray tap drops them with no easy
  // way back, so we confirm first. (Host leaves are intentional Stop/exit
  // flows handled by the parent, so we don't gate those.)
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Local active-speaker / remote active-speaker flags, driven by the
  // Web Audio analyser loop below. Used to paint a primary-colour ring
  // around whichever tile is currently talking (Zoom/Meet style).
  const [localSpeaking, setLocalSpeaking] = useState(false);
  const [remoteSpeaking, setRemoteSpeaking] = useState(false);

  // The peer's reported mic/camera state. track.enabled changes aren't
  // observable across the wire (the remote just receives silence / black
  // frames), so each side signals its own state over a WebRTC data channel
  // and we render a "muted" badge / "camera off" placeholder on the remote
  // tile. (Data channel, not the signaling WS — the rooms relay doesn't
  // forward custom message types, only offer/answer/ice.)
  const [remoteMuted, setRemoteMuted] = useState(false);
  const [remoteCameraOff, setRemoteCameraOff] = useState(false);
  const [remoteName, setRemoteName] = useState('');
  const displayRemoteName = (remoteName && remoteName.trim())
    || (peerRole === 'host' ? 'Guest' : 'Host');

  // Plain refs for non-DOM access (draw loop reads .videoWidth, etc.).
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  // Data channel for app-level metadata (mic/camera state). Set when the
  // channel opens (host creates it; guest receives via ondatachannel).
  const dcRef = useRef(null);
  // Latest local media state + name, mirrored into a ref so the data-channel
  // onopen handler can send current values without a stale closure.
  const mediaStateRef = useRef({ muted: false, cameraOff: false, name: displayLocalName });
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const audioDestRef = useRef(null);
  const recordTimerRef = useRef(null);

  // Tracks whether the PC has been 'connected' at least once during this
  // mount. Used to distinguish "couldn't connect at all" (nat-blocked)
  // from "we were connected and the peer went away" (peer-gone). Without
  // this, a transient ICE failure post-connection would mis-flag the
  // call as NAT-blocked even though it had been working.
  const everConnectedRef = useRef(false);

  // True only when the user has explicitly clicked Stop. The onstop
  // handler checks this before firing onRecordingFinalized — otherwise
  // any path that unmounts InterviewCall mid-recording (Leave button,
  // peer-rejoin remount via negotiationKey, route change) would also
  // navigate the host to the review page with whatever partial bytes
  // happened to be captured. With this guard, only an intentional Stop
  // produces a saved file; everything else discards the recording.
  const intentToSaveRef = useRef(false);

  // Ref callbacks for the <video> tags. Using callbacks (not plain refs
  // + useEffect on srcObject) is essential because layout changes move
  // the <video> element to a different parent in the JSX tree — React
  // then unmounts the old DOM node and mounts a fresh one, which has no
  // srcObject and shows black until rebound. The callback fires on
  // every mount, so srcObject is always reattached to the live DOM node.
  /* eslint-disable no-param-reassign */
  // Setting srcObject on the DOM node is the whole point of these
  // callbacks — disabling no-param-reassign for the two assignments.
  const attachLocalVideo = useCallback((el) => {
    localVideoRef.current = el;
    if (el && localStream && el.srcObject !== localStream) {
      el.srcObject = localStream;
    }
  }, [localStream]);

  const attachRemoteVideo = useCallback((el) => {
    remoteVideoRef.current = el;
    if (el && remoteStream && el.srcObject !== remoteStream) {
      el.srcObject = remoteStream;
    }
  }, [remoteStream]);
  /* eslint-enable no-param-reassign */

  // ── RTCPeerConnection lifecycle ──
  useEffect(() => {
    if (!ws || !localStream) return undefined;

    const pc = new RTCPeerConnection({ iceServers: iceServers || [] });
    pcRef.current = pc;

    localStream.getTracks().forEach((t) => pc.addTrack(t, localStream));

    pc.onicecandidate = (ev) => {
      if (ev.candidate && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'ice', payload: ev.candidate.toJSON() }));
        } catch (e) { /* noop */ }
      }
    };

    pc.ontrack = (ev) => {
      // srcObject is rebound by the ref-callback on render; we just
      // surface the stream into state and let React do the wiring.
      const [stream] = ev.streams;
      setRemoteStream(stream);
    };

    // ── Media-state data channel ──
    // The offerer (host) creates it; the answerer (guest) picks it up via
    // ondatachannel. Both sides send their own mic/camera state on open +
    // whenever it changes, and reflect the peer's onto the remote tile.
    /* eslint-disable no-param-reassign */
    const wireDataChannel = (dc) => {
      dcRef.current = dc;
      dc.onopen = () => {
        const { muted: m, cameraOff: c, name: n } = mediaStateRef.current;
        try { dc.send(JSON.stringify({ type: 'media-state', audioEnabled: !m, videoEnabled: !c, name: n })); } catch (e) { /* noop */ }
      };
      dc.onmessage = (e) => {
        try {
          const m = JSON.parse(e.data);
          if (m && m.type === 'media-state') {
            if (typeof m.audioEnabled === 'boolean') setRemoteMuted(!m.audioEnabled);
            if (typeof m.videoEnabled === 'boolean') setRemoteCameraOff(!m.videoEnabled);
            if (typeof m.name === 'string') setRemoteName(m.name);
          }
        } catch (err) { /* ignore */ }
      };
    };
    /* eslint-enable no-param-reassign */
    pc.ondatachannel = (ev) => wireDataChannel(ev.channel);
    if (peerRole === 'host') {
      try { wireDataChannel(pc.createDataChannel('meta')); } catch (e) { /* noop */ }
    }

    // We listen ONLY on connectionState (the aggregated/authoritative
    // signal). iceConnectionState transitions through 'failed' during
    // normal negotiation when individual candidate pairs lose — that
    // would cause false "Network blocked" flashes even on connections
    // that ultimately succeed via TURN. connectionState is conservative
    // and only goes 'failed' when the whole transport is dead.
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === 'connected') {
        everConnectedRef.current = true;
        setCallState('connected');
      } else if (s === 'failed') {
        // Distinguish "couldn't connect at all" from "we were connected
        // and the peer left/network died". Without the ever-connected
        // guard, a peer who drops mid-call would be mis-flagged as a
        // NAT problem.
        setCallState(everConnectedRef.current ? 'peer-gone' : 'nat-blocked');
      } else if (s === 'disconnected') {
        // Transient — don't flip UI yet; either reconnects on its own
        // (the platform retries ICE) or transitions to 'failed' next.
      }
    };

    // Belt-and-suspenders timeout. connectionState 'failed' SHOULD fire
    // on its own, but a few browser/network combos hang in 'checking'
    // forever. Only kick in if we never reached 'connected' — once we've
    // been connected, transient disconnect should not trip nat-blocked.
    const connectTimeout = setTimeout(() => {
      if (!everConnectedRef.current) {
        setCallState((prev) => (prev === 'connecting' ? 'nat-blocked' : prev));
      }
    }, 25000);

    // Pending ICE candidates that arrive before setRemoteDescription
    // completes — we buffer and flush them after.
    const pendingIce = [];

    const onMessage = async (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch (e) { return; }
      if (!msg || !msg.type) return;
      try {
        if (msg.type === 'offer' && peerRole === 'guest') {
          await pc.setRemoteDescription(msg.payload);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'answer', payload: answer }));
          }
          // Serial drain on purpose — some browsers reject candidates
          // added in parallel before the remote description is fully
          // applied. Keeping these awaits sequential is intentional.
          // eslint-disable-next-line no-await-in-loop
          while (pendingIce.length) {
            // eslint-disable-next-line no-await-in-loop
            try { await pc.addIceCandidate(pendingIce.shift()); } catch (e) { /* noop */ }
          }
        } else if (msg.type === 'answer' && peerRole === 'host') {
          await pc.setRemoteDescription(msg.payload);
          // eslint-disable-next-line no-await-in-loop
          while (pendingIce.length) {
            // eslint-disable-next-line no-await-in-loop
            try { await pc.addIceCandidate(pendingIce.shift()); } catch (e) { /* noop */ }
          }
        } else if (msg.type === 'ice') {
          if (pc.remoteDescription && pc.remoteDescription.type) {
            try { await pc.addIceCandidate(msg.payload); } catch (e) { /* noop */ }
          } else {
            pendingIce.push(msg.payload);
          }
        } else if (msg.type === 'media-state') {
          // Peer told us their mic/camera state — reflect it on their tile.
          if (typeof msg.audioEnabled === 'boolean') setRemoteMuted(!msg.audioEnabled);
          if (typeof msg.videoEnabled === 'boolean') setRemoteCameraOff(!msg.videoEnabled);
          if (typeof msg.name === 'string') setRemoteName(msg.name);
        } else if (msg.type === 'peer-left') {
          setCallState('peer-gone');
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[interview] signaling error', e);
      }
    };

    ws.addEventListener('message', onMessage);

    // Host kicks off the offer. The delay gives the WS a moment to
    // settle AND ensures the guest's <InterviewCall> has had time to
    // mount and attach its own onmessage listener — important on the
    // reconnect path where this component remounts via key change and
    // the guest's component is also mounting in parallel. Too short and
    // the offer can hit the WS before the guest is listening; the
    // parent's onmessage doesn't act on offers, so the message is lost.
    let offerTimer = null;
    if (peerRole === 'host') {
      offerTimer = setTimeout(async () => {
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'offer', payload: offer }));
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[interview] createOffer failed', e);
          setCallState('error');
        }
      }, 300);
    }

    return () => {
      ws.removeEventListener('message', onMessage);
      if (offerTimer) clearTimeout(offerTimer);
      clearTimeout(connectTimeout);
      try { pc.close(); } catch (e) { /* noop */ }
      pcRef.current = null;
    };
  }, [ws, localStream, iceServers, peerRole]);

  // ── Canvas composition + recording (host only) ──
  // The draw loop runs whenever the host is recording. It paints the
  // current layout onto the offscreen canvas every animation frame, using
  // the <video> elements already on screen as the source so we don't have
  // to manage parallel decoders.
  const drawLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const local = localVideoRef.current;
    const remote = remoteVideoRef.current;
    if (!ctx) return;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // "Contain" math (Math.min) to mirror the on-screen object-fit:contain
    // on the <video> elements. The recorded file will look exactly like
    // what the host saw during the call — full camera frame, with black
    // bars where the aspect ratios don't match the tile. Math.max here
    // would re-introduce cropping in the recording even though the live
    // preview shows the whole frame.
    const drawContain = (videoEl, dx, dy, dw, dh) => {
      if (!videoEl || !videoEl.videoWidth || !videoEl.videoHeight) {
        // Placeholder when remote hasn't sent a frame yet.
        ctx.fillStyle = '#1a1d28';
        ctx.fillRect(dx, dy, dw, dh);
        return;
      }
      const vw = videoEl.videoWidth;
      const vh = videoEl.videoHeight;
      const scale = Math.min(dw / vw, dh / vh);
      const w = vw * scale;
      const h = vh * scale;
      const x = dx + (dw - w) / 2;
      const y = dy + (dh - h) / 2;
      ctx.drawImage(videoEl, x, y, w, h);
    };

    if (layout === LAYOUTS.SIDE_BY_SIDE) {
      if (canvas.width >= canvas.height) {
        // landscape: split horizontally
        const half = canvas.width / 2;
        drawContain(local, 0, 0, half, canvas.height);
        drawContain(remote, half, 0, half, canvas.height);
      } else {
        // portrait: stack vertically
        const half = canvas.height / 2;
        drawContain(local, 0, 0, canvas.width, half);
        drawContain(remote, 0, half, canvas.width, half);
      }
    } else if (layout === LAYOUTS.PIP_LOCAL_CORNER) {
      // remote full, local pip in bottom-right
      drawContain(remote, 0, 0, canvas.width, canvas.height);
      const pipW = Math.round(canvas.width * PIP_TILE_FRACTION);
      const pipH = Math.round((pipW * 9) / 16);
      const margin = Math.round(canvas.width * 0.02);
      drawContain(local, canvas.width - pipW - margin, canvas.height - pipH - margin, pipW, pipH);
    } else if (layout === LAYOUTS.PIP_REMOTE_CORNER) {
      // local full, remote pip in bottom-right
      drawContain(local, 0, 0, canvas.width, canvas.height);
      const pipW = Math.round(canvas.width * PIP_TILE_FRACTION);
      const pipH = Math.round((pipW * 9) / 16);
      const margin = Math.round(canvas.width * 0.02);
      drawContain(remote, canvas.width - pipW - margin, canvas.height - pipH - margin, pipW, pipH);
    }

    rafRef.current = requestAnimationFrame(drawLoop);
  }, [layout]);

  // ── Recording controls ──
  const startRecording = useCallback(async () => {
    if (peerRole !== 'host' || recording) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dims = canvasDimsFor(layout, isMobile);
    canvas.width = dims.w;
    canvas.height = dims.h;

    // Mix audio: local mic + remote audio → MediaStreamDestination.
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioCtor();
    audioCtxRef.current = audioCtx;
    const dest = audioCtx.createMediaStreamDestination();
    audioDestRef.current = dest;

    const localAudio = localStream && localStream.getAudioTracks()[0];
    if (localAudio) {
      try {
        const src = audioCtx.createMediaStreamSource(new MediaStream([localAudio]));
        src.connect(dest);
      } catch (e) { /* noop */ }
    }
    if (remoteStream) {
      const remoteAudio = remoteStream.getAudioTracks()[0];
      if (remoteAudio) {
        try {
          const src = audioCtx.createMediaStreamSource(new MediaStream([remoteAudio]));
          src.connect(dest);
        } catch (e) { /* noop */ }
      }
    }

    // Start the draw loop and harvest its stream.
    rafRef.current = requestAnimationFrame(drawLoop);
    const canvasStream = canvas.captureStream(30);
    const composite = new MediaStream();
    canvasStream.getVideoTracks().forEach((t) => composite.addTrack(t));
    dest.stream.getAudioTracks().forEach((t) => composite.addTrack(t));

    // Pick a codec the browser actually supports. Order from most-
    // compatible (Chrome / Firefox / Android) to fallback (Safari).
    const candidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4', // Safari 14.1+
    ];
    let mime = '';
    for (let i = 0; i < candidates.length; i += 1) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(candidates[i])) {
        mime = candidates[i]; break;
      }
    }
    let recorder;
    try {
      recorder = mime ? new MediaRecorder(composite, { mimeType: mime }) : new MediaRecorder(composite);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[interview] MediaRecorder construct failed', e);
      return;
    }
    mediaRecorderRef.current = recorder;
    recordChunksRef.current = [];
    // Reset on every fresh recording — a new Start clears any leftover
    // intent from a previous (now-finished) recording in the same mount.
    intentToSaveRef.current = false;

    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) recordChunksRef.current.push(ev.data);
    };
    recorder.onstop = () => {
      const baseType = (mime || 'video/webm').split(';')[0];
      const ext = baseType.includes('mp4') ? 'mp4' : 'webm';
      const chunks = recordChunksRef.current;
      recordChunksRef.current = [];
      // Only finalize + hand off to the review page when the user
      // explicitly clicked Stop. Any other path (unmount on Leave, key
      // remount on peer-rejoin, route navigation) leaves intentToSave
      // false and the bytes are dropped.
      if (!intentToSaveRef.current) return;
      const blob = new Blob(chunks, { type: baseType });
      const filename = `interview-${new Date().toISOString().replace(/[:.]/g, '-')}.${ext}`;
      const file = new File([blob], filename, { type: baseType });
      if (onRecordingFinalized) onRecordingFinalized(file);
    };

    recorder.start(1000); // 1s chunks — supports future chunked-upload
    setRecording(true);
    setRecordSeconds(0);
    recordTimerRef.current = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
  }, [peerRole, recording, layout, isMobile, localStream, remoteStream, drawLoop, onRecordingFinalized]);

  const stopRecording = useCallback(() => {
    if (!recording) return;
    // Mark user intent BEFORE actually stopping so the onstop handler
    // (which can fire synchronously on some browsers) sees the flag.
    intentToSaveRef.current = true;
    setRecording(false);
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    try { if (mediaRecorderRef.current) mediaRecorderRef.current.stop(); } catch (e) { /* noop */ }
    mediaRecorderRef.current = null;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) { /* noop */ }
      audioCtxRef.current = null;
    }
    audioDestRef.current = null;
  }, [recording]);

  // Stop the recorder on unmount so we don't leave a MediaRecorder
  // running if the user navigates away mid-call.
  useEffect(() => () => {
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { if (mediaRecorderRef.current) mediaRecorderRef.current.stop(); } catch (e) { /* noop */ }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) { /* noop */ }
    }
  }, []);

  // Swap the active camera between front-facing and back-facing. We:
  //   1. open a new MediaStream just for the other-direction camera,
  //   2. swap the existing video track out of the local MediaStream,
  //   3. tell the RTCPeerConnection sender to use the new track via
  //      RTCRtpSender.replaceTrack (no re-negotiation needed),
  //   4. re-set the <video> srcObject to nudge the browser to repaint
  //      with the new track.
  // The MediaRecorder is fed by the canvas which reads from the local
  // <video> element on every frame, so it picks up the new camera
  // automatically — no recorder restart, no recording interruption.
  const switchCamera = useCallback(async () => {
    if (!localStream || switchingCamera) return;
    setSwitchingCamera(true);
    const next = facingMode === 'user' ? 'environment' : 'user';
    const oldVideo = localStream.getVideoTracks()[0];
    const baseRes = { width: { ideal: 1280 }, height: { ideal: 720 } };

    // Build an ordered list of constraints to try. Android (notably
    // Samsung) frequently IGNORES `facingMode: { ideal }` and hands back
    // the same lens, which is why the switch "did nothing" there while
    // iOS honoured it. So we try, in order:
    //   1. facingMode { exact } — forces the flip where supported.
    //   2. an explicit different deviceId — the most reliable fallback;
    //      we enumerate cameras and pick one whose id ≠ the current lens.
    //   3. facingMode { ideal } — last resort for devices that only honour
    //      the soft hint (and the original iOS-friendly path).
    const attempts = [{ video: { ...baseRes, facingMode: { exact: next } }, audio: false }];
    try {
      const cams = (await navigator.mediaDevices.enumerateDevices())
        .filter((d) => d.kind === 'videoinput');
      const currentId = oldVideo && oldVideo.getSettings ? oldVideo.getSettings().deviceId : null;
      const other = cams.find((c) => c.deviceId && c.deviceId !== currentId);
      if (other) {
        attempts.push({ video: { ...baseRes, deviceId: { exact: other.deviceId } }, audio: false });
      }
    } catch (e) { /* enumerateDevices can be empty pre-grant; ignore */ }
    attempts.push({ video: { ...baseRes, facingMode: { ideal: next } }, audio: false });

    let newStream = null;
    try {
      for (let i = 0; i < attempts.length && !newStream; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          newStream = await navigator.mediaDevices.getUserMedia(attempts[i]);
        } catch (e) {
          // Some Android devices can't open the 2nd camera while the 1st
          // is still live (NotReadableError / AbortError). Stop the old
          // track and retry the SAME constraint once before moving on.
          if ((e.name === 'NotReadableError' || e.name === 'AbortError')
              && oldVideo && oldVideo.readyState === 'live') {
            try { oldVideo.stop(); } catch (err) { /* noop */ }
            try {
              // eslint-disable-next-line no-await-in-loop
              newStream = await navigator.mediaDevices.getUserMedia(attempts[i]);
            } catch (err) { /* fall through to the next attempt */ }
          }
        }
      }

      const newVideoTrack = newStream && newStream.getVideoTracks()[0];
      if (!newVideoTrack) {
        // Nothing worked (likely a single-camera device). Leave whatever
        // is still live in place.
        if (newStream) newStream.getTracks().forEach((t) => { try { t.stop(); } catch (e) { /* noop */ } });
        return;
      }

      // Carry the current camera-off state onto the new track so a swap
      // mid-"camera off" doesn't silently turn the camera back on.
      newVideoTrack.enabled = !cameraOff;

      // (3) Swap track on the PC sender so the remote peer sees the new
      // camera without re-offer/answer.
      if (pcRef.current) {
        const sender = pcRef.current.getSenders().find(
          (s) => s.track && s.track.kind === 'video',
        );
        if (sender) {
          try { await sender.replaceTrack(newVideoTrack); } catch (e) { /* noop */ }
        }
      }
      // (2) Swap into the existing MediaStream (same reference, so the
      // parent + canvas-recording pipeline don't need to know).
      const cur = localStream.getVideoTracks()[0];
      if (cur && cur !== newVideoTrack) {
        try { localStream.removeTrack(cur); } catch (e) { /* noop */ }
        try { cur.stop(); } catch (e) { /* noop */ }
      }
      localStream.addTrack(newVideoTrack);
      // (4) Force a repaint of the local preview.
      if (localVideoRef.current) {
        // eslint-disable-next-line no-param-reassign
        localVideoRef.current.srcObject = localStream;
      }
      setFacingMode(next);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[interview] switchCamera failed', e);
      if (newStream) {
        newStream.getTracks().forEach((t) => { try { t.stop(); } catch (err) { /* noop */ } });
      }
    } finally {
      setSwitchingCamera(false);
    }
  }, [localStream, facingMode, switchingCamera, cameraOff]);

  // Toggle the local mic. Flipping `track.enabled` (not stopping the
  // track) silences outgoing audio while keeping the mic active so the
  // browser's AEC keeps doing its job. The remote peer hears silence
  // until we toggle back on.
  const toggleMute = useCallback(() => {
    const audio = localStream && localStream.getAudioTracks()[0];
    if (!audio) return;
    audio.enabled = !audio.enabled;
    setMuted(!audio.enabled);
  }, [localStream]);

  // Camera on/off. Flips `track.enabled` (not stop) so the same track keeps
  // flowing to the peer — disabled just makes it emit black frames, which
  // is exactly "camera off" for the remote and the recording. We overlay a
  // local placeholder so this side doesn't just stare at a black tile.
  // (state declared near the other toggles up top so switchCamera can read it)
  const toggleCamera = useCallback(() => {
    const video = localStream && localStream.getVideoTracks()[0];
    if (!video) return;
    video.enabled = !video.enabled;
    setCameraOff(!video.enabled);
  }, [localStream]);

  // Keep the ref in sync so the data-channel onopen handler can read the
  // current state without a stale closure.
  useEffect(() => {
    mediaStateRef.current = { muted, cameraOff, name: displayLocalName };
  }, [muted, cameraOff, displayLocalName]);

  // Broadcast our own mic/camera state + name to the peer whenever it changes
  // (and on (re)connect, via the callState dep). Prefer the data channel (the
  // rooms WS relay only forwards offer/answer/ice); fall back to the WS in
  // case a future relay does forward it.
  useEffect(() => {
    const payload = JSON.stringify({ type: 'media-state', audioEnabled: !muted, videoEnabled: !cameraOff, name: displayLocalName });
    const dc = dcRef.current;
    if (dc && dc.readyState === 'open') {
      try { dc.send(payload); return; } catch (e) { /* fall through to ws */ }
    }
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(payload); } catch (e) { /* noop */ }
    }
  }, [muted, cameraOff, callState, ws, displayLocalName]);

  // ── Active-speaker detection ──
  // A lightweight, recording-independent Web Audio graph that watches the
  // RMS level of each participant's audio and flips a "speaking" flag.
  // Used purely for the on-screen tile ring (Zoom/Meet active-speaker
  // glow) — it never touches the recording mix, which has its own
  // AudioContext over in startRecording. We rebuild it whenever a stream
  // reference changes (remote arrives, camera swap re-keys the mount).
  useEffect(() => {
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor) return undefined;
    if (!localStream && !remoteStream) return undefined;
    const ctx = new AudioCtor();
    // Autoplay policy can park the context in 'suspended', which freezes
    // the analyser at a flat 128 (silence). Nudge it awake.
    if (ctx.state === 'suspended') { try { ctx.resume(); } catch (e) { /* noop */ } }

    const sources = [];
    const addSource = (stream, setter) => {
      const track = stream && stream.getAudioTracks()[0];
      if (!track) return;
      try {
        const node = ctx.createMediaStreamSource(new MediaStream([track]));
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.6;
        node.connect(analyser);
        sources.push({
          analyser,
          data: new Uint8Array(analyser.frequencyBinCount),
          setter,
          hold: 0,
          last: false,
        });
      } catch (e) { /* noop */ }
    };
    addSource(localStream, setLocalSpeaking);
    addSource(remoteStream, setRemoteSpeaking);

    const THRESHOLD = 0.05; // RMS (0..1) above which we count it as speech
    const HOLD_FRAMES = 12; // ~200ms of latch so the ring doesn't strobe
    let raf = 0;
    const tick = () => {
      for (let i = 0; i < sources.length; i += 1) {
        const s = sources[i];
        s.analyser.getByteTimeDomainData(s.data);
        let sum = 0;
        for (let j = 0; j < s.data.length; j += 1) {
          const v = (s.data[j] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / s.data.length);
        if (rms > THRESHOLD) s.hold = HOLD_FRAMES;
        else if (s.hold > 0) s.hold -= 1;
        const speaking = s.hold > 0;
        if (speaking !== s.last) {
          s.last = speaking;
          s.setter(speaking);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      setLocalSpeaking(false);
      setRemoteSpeaking(false);
      try { ctx.close(); } catch (e) { /* noop */ }
    };
  }, [localStream, remoteStream]);

  const fmtTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const r = (s % 60).toString().padStart(2, '0');
    return `${m}:${r}`;
  };

  // A muted mic still produces silence into the analyser, but gate
  // explicitly so the ring never lingers from a pre-mute latch frame.
  const localActive = localSpeaking && !muted;
  const remoteActive = remoteSpeaking;

  // Primary-colour ring shared by every tile. Spread into a tile's style;
  // falls back to `baseBorder` (each tile's own resting border) when the
  // participant isn't talking so the layout never shifts.
  const speakingRing = (active, baseBorder) => ({
    border: active ? '2px solid #5aa5ef' : baseBorder,
    boxShadow: active
      ? '0 0 0 1px #5aa5ef, 0 0 18px rgba(90, 165, 239, 0.55)'
      : 'none',
    transition: 'box-shadow 0.12s ease, border-color 0.12s ease',
  });

  // ── Render ──
  // Full-viewport non-scrollable layout. The page is split into three
  // rows: a thin top bar (status + layout switcher), the stage (fills
  // remaining space), and a bottom controls bar (mute + record + leave).
  // The stage flips flexDirection based on viewport orientation, so two
  // portrait phone cameras stack vertically and two landscape laptops
  // sit side by side — same component, no fixed aspect ratios, no
  // scroll regardless of orientation.

  // Mic-off badge pinned to the TOP-LEFT of a tile (Zoom convention —
  // shown on every viewer's copy of that person's tile, including their
  // own). `mutedBadge(true)` renders it; falsy renders nothing.
  const mutedBadge = (show) => (show ? (
    <span
      aria-label="Muted"
      title="Muted"
      style={{
        position: 'absolute',
        top: '8px',
        left: '8px',
        zIndex: 2,
        width: '26px',
        height: '26px',
        borderRadius: '999px',
        background: 'rgba(255, 77, 79, 0.92)',
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="1" y1="1" x2="23" y2="23" />
        <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
        <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
        <line x1="12" y1="19" x2="12" y2="23" />
      </svg>
    </span>
  ) : null);

  // Camera-off tile: a letter avatar (first initial) + the name — instead
  // of a camera-off icon. `cameraOffPlaceholder(true, 'Guest')` renders it.
  const cameraOffPlaceholder = (show, name) => (show ? (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        // No z-index on purpose: it must cover its OWN <video> (an earlier
        // sibling) but stay BELOW later siblings like the PIP corner tile —
        // a z-index here would paint it over the corner and hide it.
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        background: '#14161d',
        color: '#e6e7ea',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 'clamp(52px, 22%, 92px)',
          aspectRatio: '1 / 1',
          borderRadius: '999px',
          background: 'linear-gradient(135deg, #5aa5ef, #3f78c9)',
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'clamp(1.4rem, 6vw, 2.4rem)',
          fontWeight: 800,
          textTransform: 'uppercase',
        }}
      >
        {(name || '?').trim().charAt(0) || '?'}
      </span>
      <span style={{ fontSize: '0.9rem', fontWeight: 700 }}>{name}</span>
    </div>
  ) : null);

  const localVideoTag = (
    <>
      <video
        ref={attachLocalVideo}
        autoPlay
        muted
        playsInline
        // contain (not cover): show the whole camera frame, letterbox the
        // tile background where needed. Avoids cropping a portrait phone
        // camera inside a landscape tile or vice versa.
        // Mirror only the front camera — the back camera shows the world
        // as-is and mirroring would feel like a fun-house mirror.
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
          // Hidden (not unmounted) while the camera is off so the track
          // stays attached and flips straight back on toggle.
          visibility: cameraOff ? 'hidden' : 'visible',
        }}
      />
      {cameraOffPlaceholder(cameraOff, displayLocalName)}
      {mutedBadge(muted)}
    </>
  );

  // Remote audio is intentionally NOT muted — the host needs to hear the
  // guest. AEC in getUserMedia (enabled in the parent) prevents the
  // playback from being recorded back through the mic.
  const remoteVideoTag = (
    <>
      <video
        ref={attachRemoteVideo}
        autoPlay
        playsInline
        // contain (not cover): see comment on local video tag above.
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          visibility: remoteCameraOff ? 'hidden' : 'visible',
        }}
      />
      {cameraOffPlaceholder(remoteCameraOff, displayRemoteName)}
      {mutedBadge(remoteMuted)}
    </>
  );

  const tile = (videoEl, label, k, active) => (
    <div
      key={k}
      style={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        background: '#000',
        borderRadius: '12px',
        overflow: 'hidden',
        ...speakingRing(active, '1px solid rgba(255,255,255,0.10)'),
      }}
    >
      {videoEl}
      <span
        style={{
          position: 'absolute',
          bottom: '8px',
          left: '8px',
          padding: '3px 9px',
          background: 'rgba(0,0,0,0.55)',
          borderRadius: '999px',
          fontSize: '0.72rem',
          fontWeight: 700,
          color: '#fff',
        }}
      >
        {label}
      </span>
    </div>
  );

  const stage = layout === LAYOUTS.SIDE_BY_SIDE ? (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: isPortrait ? 'column' : 'row',
        gap: '8px',
      }}
    >
      {tile(localVideoTag, displayLocalName, 'l-sbs', localActive)}
      {tile(remoteVideoTag, displayRemoteName, 'r-sbs', remoteActive)}
    </div>
  ) : (
    // PIP modes — big tile fills the stage, small overlay in the corner.
    // The big tile shows the remote peer in "Guest big", the local peer
    // in "You big"; the corner shows whoever isn't in the big tile. We
    // map the active-speaker flags accordingly so the ring lands on the
    // right face regardless of which way the PIP is flipped.
    (() => {
      const bigIsRemote = layout === LAYOUTS.PIP_LOCAL_CORNER;
      const bigActive = bigIsRemote ? remoteActive : localActive;
      const cornerActive = bigIsRemote ? localActive : remoteActive;
      return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        position: 'relative',
        borderRadius: '12px',
        overflow: 'hidden',
        background: '#000',
        ...speakingRing(bigActive, '1px solid rgba(255,255,255,0.10)'),
      }}
    >
      {layout === LAYOUTS.PIP_LOCAL_CORNER ? remoteVideoTag : localVideoTag}
      <span
        style={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          padding: '3px 9px',
          background: 'rgba(0,0,0,0.55)',
          borderRadius: '999px',
          fontSize: '0.72rem',
          fontWeight: 700,
          color: '#fff',
        }}
      >
        {layout === LAYOUTS.PIP_LOCAL_CORNER ? displayRemoteName : displayLocalName}
      </span>
      <div
        style={{
          position: 'absolute',
          right: '10px',
          bottom: '10px',
          // Corner tile uses a viewport-min so it scales sanely on both
          // portrait phones and ultrawide desktops.
          width: 'min(32vw, 200px)',
          aspectRatio: '3 / 4',
          borderRadius: '10px',
          overflow: 'hidden',
          background: '#000',
          // Keep the resting drop-shadow for depth; layer the speaking
          // glow on top of it when the corner participant is talking.
          border: cornerActive ? '2px solid #5aa5ef' : '2px solid rgba(255,255,255,0.45)',
          boxShadow: cornerActive
            ? '0 0 0 1px #5aa5ef, 0 0 18px rgba(90, 165, 239, 0.55), 0 6px 18px rgba(0,0,0,0.55)'
            : '0 6px 18px rgba(0,0,0,0.55)',
          transition: 'box-shadow 0.12s ease, border-color 0.12s ease',
        }}
      >
        {layout === LAYOUTS.PIP_LOCAL_CORNER ? localVideoTag : remoteVideoTag}
      </div>
    </div>
      );
    })()
  );

  const layoutPicker = (
    <div
      style={{
        display: 'flex',
        gap: '4px',
        flexWrap: 'nowrap',
        justifyContent: 'flex-end',
      }}
    >
      {[
        { id: LAYOUTS.SIDE_BY_SIDE, label: isMobile ? 'Both' : 'Side by side' },
        { id: LAYOUTS.PIP_LOCAL_CORNER, label: isMobile ? 'Guest' : 'Guest big' },
        { id: LAYOUTS.PIP_REMOTE_CORNER, label: isMobile ? 'You' : 'You big' },
      ].map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => setLayout(opt.id)}
          // Layout change mid-recording would resize the canvas and
          // break the encoder. Lock it while recording.
          disabled={recording}
          style={{
            padding: isMobile ? '6px 10px' : '6px 12px',
            borderRadius: '999px',
            border: layout === opt.id ? '1px solid #5aa5ef' : '1px solid rgba(255,255,255,0.18)',
            background: layout === opt.id ? 'rgba(90,165,239,0.16)' : 'rgba(255,255,255,0.05)',
            color: layout === opt.id ? '#fff' : '#cfd1da',
            fontFamily: 'inherit',
            fontSize: isMobile ? '0.72rem' : '0.78rem',
            fontWeight: 700,
            cursor: recording ? 'not-allowed' : 'pointer',
            opacity: recording && layout !== opt.id ? 0.5 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  // Status text — collapses tighter on mobile so it doesn't crowd out
  // the layout picker on a narrow top bar.
  const statusText = (() => {
    if (callState === 'connected' && recording) return `Rec ${fmtTime(recordSeconds)}`;
    if (callState === 'connected') return 'Connected';
    if (callState === 'connecting') return 'Connecting…';
    if (callState === 'peer-gone') return peerRole === 'host' ? 'Guest disconnected' : 'Host disconnected';
    if (callState === 'nat-blocked') return 'Network blocked';
    if (callState === 'error') return 'Connection error';
    return '';
  })();
  const statusGood = callState === 'connected';

  const iconBtn = (active, disabled) => ({
    width: isMobile ? '44px' : '48px',
    height: isMobile ? '44px' : '48px',
    borderRadius: '999px',
    border: 'none',
    background: active ? '#ff4d4f' : 'rgba(255,255,255,0.10)',
    color: '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: active ? '0 6px 18px rgba(255, 77, 79, 0.35)' : 'none',
    fontFamily: 'inherit',
  });

  const muteIcon = muted ? (
    // Mic muted (with slash)
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
      <line x1="12" y1="19" x2="12" y2="23" />
    </svg>
  ) : (
    // Mic on
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
    </svg>
  );

  const leaveIcon = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21 5h-9a3 3 0 0 0-3 3v3h2V8a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-3H9v3a3 3 0 0 0 3 3h9a3 3 0 0 0 3-3V8a3 3 0 0 0-3-3zM10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59z" />
    </svg>
  );

  const cameraIcon = cameraOff ? (
    // Camera off (with slash)
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    // Camera on
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 7l-7 5 7 5V7z" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );

  // Camera-flip icon: a camera body with circular arrows wrapping around
  // it. Conveys "switch camera" universally on mobile.
  const flipCameraIcon = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 4h-3.17L15 2H9L7.17 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
      <path d="M9.5 13 7 10.5 9.5 8" />
      <path d="M14.5 11l2.5 2.5L14.5 16" />
      <path d="M7 10.5h7a3 3 0 0 1 3 3" />
    </svg>
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#0a0c12',
        color: '#fff',
        fontFamily: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        // 100dvh on supporting browsers handles mobile URL bars correctly
        // (the URL bar collapses when scrolling on iOS Safari, shifting
        // 100vh; 100dvh tracks the *current* dynamic viewport).
        height: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
        zIndex: 100,
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          padding: isMobile ? '10px 12px' : '12px 18px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: statusGood ? '#bce8c2' : '#ffd166',
            fontSize: '0.85rem',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '999px',
              background: statusGood ? '#5ad77a' : '#ffd166',
              boxShadow: statusGood
                ? '0 0 0 5px rgba(90, 215, 122, 0.18)'
                : '0 0 0 5px rgba(255, 209, 102, 0.18)',
              flexShrink: 0,
            }}
          />
          {statusText}
        </div>
        {layoutPicker}
      </div>

      {/* Stage */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          padding: isMobile ? '8px' : '12px',
          display: 'flex',
        }}
      >
        {stage}
      </div>

      {/* Hidden offscreen canvas used only during recording. */}
      <canvas ref={canvasRef} style={{ display: 'none' }} width={1280} height={720} />

      {/* Bottom controls */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? '14px' : '20px',
          padding: isMobile ? '10px 12px calc(10px + env(safe-area-inset-bottom))' : '14px 18px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
          title={muted ? 'Unmute' : 'Mute'}
          style={iconBtn(muted, false)}
        >
          {muteIcon}
        </button>

        <button
          type="button"
          onClick={toggleCamera}
          aria-label={cameraOff ? 'Turn camera on' : 'Turn camera off'}
          title={cameraOff ? 'Camera on' : 'Camera off'}
          style={iconBtn(cameraOff, false)}
        >
          {cameraIcon}
        </button>

        {/* Camera switch — only on mobile-sized viewports, where the
            device is likely to have both a front- and back-facing
            camera. Desktops with a single webcam don't need it, and
            even on desktops with multiple webcams, facingMode hints
            don't usually do anything meaningful — better to hide. */}
        {isMobile && (
          <button
            type="button"
            onClick={switchCamera}
            disabled={switchingCamera}
            aria-label="Switch camera"
            title={facingMode === 'user' ? 'Switch to back camera' : 'Switch to front camera'}
            style={iconBtn(false, switchingCamera)}
          >
            {flipCameraIcon}
          </button>
        )}

        {peerRole === 'host' && (
          !recording ? (
            <button
              type="button"
              onClick={startRecording}
              disabled={callState !== 'connected'}
              style={{
                ...primaryBtn(callState !== 'connected'),
                padding: isMobile ? '12px 22px' : '14px 32px',
                fontSize: isMobile ? '0.9rem' : '1rem',
                minHeight: isMobile ? '44px' : '48px',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '999px',
                  background: '#ff4d4f',
                  display: 'inline-block',
                  marginRight: '8px',
                }}
              />
              Record
            </button>
          ) : (
            <button
              type="button"
              onClick={stopRecording}
              style={{
                ...primaryBtn(false),
                background: '#ff4d4f',
                boxShadow: '0 8px 22px rgba(255, 77, 79, 0.40)',
                padding: isMobile ? '12px 22px' : '14px 32px',
                fontSize: isMobile ? '0.9rem' : '1rem',
                minHeight: isMobile ? '44px' : '48px',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: '10px',
                  height: '10px',
                  background: '#fff',
                  display: 'inline-block',
                  borderRadius: '2px',
                  marginRight: '8px',
                }}
              />
              Stop
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => (peerRole === 'guest' ? setShowLeaveConfirm(true) : onLeave())}
          aria-label="Leave"
          title="Leave"
          style={{
            ...iconBtn(false, false),
            background: 'rgba(255, 77, 79, 0.85)',
          }}
        >
          {leaveIcon}
        </button>
      </div>

      {/* Leave confirmation — guest only. Prevents a mis-tap from dropping
          the guest out of a one-time link with no quick way back in. */}
      {showLeaveConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Leave the meeting?"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            background: 'rgba(5, 6, 10, 0.72)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '380px',
              background: '#161922',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: '16px',
              padding: isMobile ? '22px 20px' : '26px 24px',
              textAlign: 'center',
              boxShadow: '0 18px 50px rgba(0,0,0,0.55)',
            }}
          >
            <div aria-hidden="true" style={{ fontSize: '2.4rem', lineHeight: 1, marginBottom: '10px' }}>
              👋
            </div>
            <h2 style={{ margin: 0, fontSize: isMobile ? '1.2rem' : '1.35rem', fontWeight: 800 }}>
              Leave the meeting?
            </h2>
            <p
              style={{
                margin: '10px auto 0',
                maxWidth: '300px',
                color: '#b9bdc7',
                fontSize: '0.92rem',
                lineHeight: 1.45,
              }}
            >
              You&apos;ll be disconnected from the call.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '22px' }}>
              <button
                type="button"
                onClick={() => setShowLeaveConfirm(false)}
                style={{ ...secondaryBtn, flex: 1 }}
              >
                Stay
              </button>
              <button
                type="button"
                onClick={() => { setShowLeaveConfirm(false); onLeave(); }}
                style={{
                  ...primaryBtn(false),
                  flex: 1,
                  padding: '12px 20px',
                  background: '#ff4d4f',
                  boxShadow: '0 8px 22px rgba(255, 77, 79, 0.40)',
                }}
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewCall;
